import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ImageUploaderComponent } from '../../components/image-uploader/image-uploader.component';
import { BannerService } from '../../core/services/banner.service';
import { Banner } from '../../models/banner.interface';

@Component({
  selector: 'app-banner-form',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SidebarComponent, NavbarComponent, ImageUploaderComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar 
      [pageTitle]="isEditMode ? 'Edit Hero Banner' : 'Add New Hero Banner'" 
      [pageSubtitle]="isEditMode ? 'Update homepage slider imagery & copy' : 'Create new homepage hero slide with Cloudinary upload'"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">
      <div class="form-card glass-card">
        <div class="form-header">
          <h2 class="font-serif title-gold">{{ isEditMode ? 'Edit Slide Details' : 'New Banner Specs' }}</h2>
          <a routerLink="/banners" class="btn-secondary">← Back to Banners</a>
        </div>

        <form (ngSubmit)="onSubmit()" class="banner-form">
          <div class="form-grid">
            <div class="form-column">
              <div class="form-group">
                <label class="form-label">Banner Heading *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="banner.heading" 
                  name="heading" 
                  placeholder="e.g. phone cases that stand out" 
                  required 
                />
              </div>

              <div class="form-group">
                <label class="form-label">Badge Tag</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="banner.badge" 
                  name="badge" 
                  placeholder="LIMITED EDITION DROP, HAUTE COUTURE 2026" 
                />
              </div>

              <div class="form-group">
                <label class="form-label">Subtitle / Offer Copy</label>
                <textarea 
                  class="form-control" 
                  rows="2" 
                  [(ngModel)]="banner.sub" 
                  name="sub" 
                  placeholder="buy 1 get 1 free. only today."
                ></textarea>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">CTA Button Text</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    [(ngModel)]="banner.cta" 
                    name="cta" 
                    placeholder="Explore Collection" 
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Slide Order</label>
                  <input 
                    type="number" 
                    class="form-control" 
                    [(ngModel)]="banner.order" 
                    name="order" 
                    placeholder="1" 
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Banner Placement *</label>
                <select class="form-control" [(ngModel)]="banner.placement" name="placement">
                  <option value="home">🏠 Home Page Hero Slider</option>
                  <option value="collection">📂 Collections Page Hero</option>
                  <option value="product">🛍️ Product Page Slider</option>
                  <option value="shop">🛒 Shop Page Slider</option>
                  <option value="both">🌐 All Pages (Global Slider)</option>
                </select>
                <span class="sub-text" style="font-size:11px;color:var(--text-muted);margin-top:4px;display:block">
                  Choose whether this slide appears on Home, Collections, Product Page, Shop, or All.
                </span>
              </div>

              <div class="form-group">
                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="banner.active" name="active" />
                  <span>Active & Visible on Website</span>
                </label>
              </div>
            </div>

            <!-- RIGHT COLUMN: CLOUDINARY UPLOAD -->
            <div class="form-column">
              <!-- Desktop Banner Upload -->
              <div class="upload-section glass-box">
                <div class="section-title-row">
                  <span class="icon-label">🖥️</span>
                  <div>
                    <label class="form-label" style="margin-bottom:2px">Desktop Hero Banner (16:9 / Landscape) *</label>
                    <span class="sub-text" style="font-size:11px;color:var(--text-muted)">Displays on desktops, laptops, and wide screens.</span>
                  </div>
                </div>
                <app-image-uploader
                  [currentImageUrl]="banner.imageUrl || ''"
                  [cloudinaryId]="banner.cloudinaryId || ''"
                  (imageUploaded)="onDesktopImageUploaded($event)"
                  (imageRemoved)="onDesktopImageRemoved()"
                ></app-image-uploader>
                <div class="form-group" style="margin-top:10px">
                  <input 
                    type="text" 
                    class="form-control" 
                    [(ngModel)]="banner.imageUrl" 
                    name="imageUrl" 
                    placeholder="Desktop image URL (https://res.cloudinary.com/...)" 
                    required
                  />
                </div>
              </div>

              <!-- Mobile Banner Upload -->
              <div class="upload-section glass-box" style="margin-top:16px">
                <div class="section-title-row">
                  <span class="icon-label">📱</span>
                  <div>
                    <label class="form-label" style="margin-bottom:2px">Mobile Hero Banner (9:16 / Portrait)</label>
                    <span class="sub-text" style="font-size:11px;color:var(--text-muted)">Optimized for smartphones. Fallback uses desktop image if empty.</span>
                  </div>
                </div>
                <app-image-uploader
                  [currentImageUrl]="banner.mobileImageUrl || ''"
                  [cloudinaryId]="banner.mobileCloudinaryId || ''"
                  (imageUploaded)="onMobileImageUploaded($event)"
                  (imageRemoved)="onMobileImageRemoved()"
                ></app-image-uploader>
                <div class="form-group" style="margin-top:10px">
                  <input 
                    type="text" 
                    class="form-control" 
                    [(ngModel)]="banner.mobileImageUrl" 
                    name="mobileImageUrl" 
                    placeholder="Mobile image URL (https://res.cloudinary.com/...)" 
                  />
                </div>
              </div>

              <!-- LIVE BANNER PREVIEW -->
              <div class="preview-box">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
                  <span class="sub-text" style="font-weight:700">Live Slider Preview</span>
                  <div class="preview-tabs">
                    <button type="button" class="preview-tab-btn" [class.active]="previewMode === 'desktop'" (click)="previewMode = 'desktop'">
                      🖥️ Desktop
                    </button>
                    <button type="button" class="preview-tab-btn" [class.active]="previewMode === 'mobile'" (click)="previewMode = 'mobile'">
                      📱 Mobile
                    </button>
                  </div>
                </div>

                <!-- Desktop preview -->
                <div *ngIf="previewMode === 'desktop'" class="banner-live-preview" [style.backgroundImage]="'url(' + (banner.imageUrl || '/images/Homepage_Banner_3.webp') + ')'">
                  <div class="preview-overlay">
                    <span class="preview-badge">{{ banner.badge || 'HERO DROP' }}</span>
                    <h3 class="preview-heading font-serif">{{ banner.heading || 'banner title' }}</h3>
                    <p class="preview-sub">{{ banner.sub || 'subtitle text' }}</p>
                  </div>
                </div>

                <!-- Mobile preview -->
                <div *ngIf="previewMode === 'mobile'" style="display:flex;justify-content:center;padding:10px 0">
                  <div class="banner-live-preview mobile-preview" [style.backgroundImage]="'url(' + (banner.mobileImageUrl || banner.imageUrl || '/images/Homepage_Banner_3.webp') + ')'">
                    <div class="preview-overlay">
                      <span class="preview-badge">{{ banner.badge || 'HERO DROP' }}</span>
                      <h3 class="preview-heading font-serif" style="font-size:13px">{{ banner.heading || 'banner title' }}</h3>
                      <p class="preview-sub" style="font-size:9.5px">{{ banner.sub || 'subtitle text' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-footer">
            <button type="button" routerLink="/banners" class="btn-secondary">Cancel</button>
            <button type="submit" class="btn-gold" [disabled]="saving">
              <span *ngIf="!saving">💾 {{ isEditMode ? 'Update Banner' : 'Save Banner' }}</span>
              <span *ngIf="saving">Saving Slide...</span>
            </button>
          </div>
        </form>
      </div>
      </main>
    </div>
  `,
  styles: [`
    .main-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .form-card {
      padding: 32px;
      max-width: 920px;
      margin: 0 auto;
    }

    .form-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
    }

    @media (max-width: 840px) {
      .form-grid { grid-template-columns: 1fr; }
    }

    .form-column {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .glass-box {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding: 16px;
    }

    .section-title-row {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
    }

    .icon-label {
      font-size: 20px;
      line-height: 1;
    }

    .preview-tabs {
      display: flex;
      gap: 4px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: var(--radius-sm);
      padding: 2px;
    }

    .preview-tab-btn {
      background: transparent;
      border: none;
      color: var(--text-secondary);
      font-size: 11px;
      font-weight: 600;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .preview-tab-btn.active {
      background: var(--accent-gold);
      color: #000;
      font-weight: 700;
    }

    .mobile-preview {
      width: 190px !important;
      height: 320px !important;
      border-radius: 18px !important;
      border: 3px solid rgba(255, 255, 255, 0.2);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-row .form-group {
      flex: 1;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: var(--text-primary);
      cursor: pointer;
      margin-top: 8px;
    }

    .checkbox-label input {
      width: 16px;
      height: 16px;
      accent-color: var(--accent-gold);
    }

    .preview-box {
      margin-top: 12px;
      padding: 16px;
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      background: rgba(0,0,0,0.3);
    }

    .banner-live-preview {
      height: 140px;
      border-radius: var(--radius-md);
      margin-top: 8px;
      background-size: cover;
      background-position: center;
      position: relative;
      overflow: hidden;
    }

    .preview-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%);
      padding: 16px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      color: #FFF;
    }

    .preview-badge {
      font-size: 9px;
      font-weight: 700;
      color: var(--accent-gold);
      letter-spacing: 0.1em;
    }

    .preview-heading {
      font-size: 16px;
      font-weight: 700;
      text-transform: lowercase;
    }

    .preview-sub {
      font-size: 11px;
      color: rgba(255,255,255,0.8);
    }

    .form-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
      margin-top: 32px;
      padding-top: 20px;
      border-top: 1px solid var(--border-color);
    }
  `]
})
export class BannerFormComponent implements OnInit {
  isEditMode = false;
  bannerId: string | null = null;
  saving = false;
  previewMode: 'desktop' | 'mobile' = 'desktop';

  banner: Partial<Banner> = {
    badge: 'LIMITED EDITION DROP',
    heading: '',
    sub: '',
    cta: 'Explore Collection',
    imageUrl: '',
    cloudinaryId: '',
    mobileImageUrl: '',
    mobileCloudinaryId: '',
    order: 1,
    placement: 'home',
    active: true
  };

  constructor(
    private bannerService: BannerService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.bannerId = this.route.snapshot.paramMap.get('id');
    if (this.bannerId) {
      this.isEditMode = true;
      this.loadBanner(this.bannerId);
    }
  }

  loadBanner(id: string): void {
    this.bannerService.getBannerById(id).subscribe({
      next: (data) => (this.banner = data),
      error: (err) => alert('Failed to load banner: ' + err.message)
    });
  }

  onDesktopImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.banner.imageUrl = data.url;
    this.banner.cloudinaryId = data.cloudinaryId;
  }

  onDesktopImageRemoved(): void {
    this.banner.imageUrl = '';
    this.banner.cloudinaryId = '';
  }

  onMobileImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.banner.mobileImageUrl = data.url;
    this.banner.mobileCloudinaryId = data.cloudinaryId;
  }

  onMobileImageRemoved(): void {
    this.banner.mobileImageUrl = '';
    this.banner.mobileCloudinaryId = '';
  }

  onSubmit(): void {
    if (!this.banner.heading || !this.banner.imageUrl) {
      alert('Please fill out Banner Heading and upload an Image');
      return;
    }

    this.saving = true;

    if (this.isEditMode && this.bannerId) {
      this.bannerService.updateBanner(this.bannerId, this.banner).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/banners']);
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to update banner: ' + err.message);
        }
      });
    } else {
      this.bannerService.createBanner(this.banner).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/banners']);
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to create banner: ' + err.message);
        }
      });
    }
  }
}
